// Declarative inventory of the home-infra boxes — the single source of truth
// for the Overview dashboard ("what lives on which box").
//
// HOME-185: `services` per box is no longer hand-maintained here. It's
// GENERATED from the Luke/Leia/Cal Ansible playbooks and the compose files
// they deploy (`web/scripts/generate-infra.mjs` → `infra.generated.ts`,
// regenerate with `npm run generate:infra`; wired as a pre-step on
// `build`/`test`/`start` so it's always fresh, and committed so CI and local
// agree on what was generated and when).
//
// MEMBERSHIP OF A BOX'S SERVICE SET IS NON-OVERRIDABLE: every container
// defined in that box's playbook/compose is guaranteed to appear, full stop.
// This file only carries the editorial bits that have no sensible home in a
// playbook — box name/role/responsibilities prose, and the status/volumes
// endpoints. It cannot add, remove, or rename a generated service. Per-service
// enrichment (label/note/kind) lives as `@overview` comments directly above
// the relevant task/service in the source files — see infraParser.mjs's
// header comment for the grammar.
//
// A bare, unadorned chip (no label/note) is not a defect: it's the generator
// telling you a service exists that nobody has annotated yet.
import { GENERATED_BOXES } from './infra.generated';

// Fullness thresholds for the volume disk-full indicators on the Overview
// dashboard (HOME-187) — percent-used at which a volume/mount bar turns
// amber, then red. Kept here (not hardcoded in the component) so the
// tripwire can be tuned without touching rendering code.
export const VOLUME_FULLNESS_THRESHOLDS = { amber: 80, red: 90 };

export type ServiceKind = 'container' | 'timer' | 'host';

export interface ServiceActionParam {
    /** JSON field name sent in the request body, e.g. "repo". */
    name: string;
    /** Label shown next to the picker. */
    label: string;
    /** Fixed choice list — mirrors the allowlist host-broker itself enforces
     *  (see `nexus_cache_invalidate.rs::ALLOWED_REPOS`). Not authoritative;
     *  the broker is the actual source of truth and will reject anything it
     *  doesn't recognise regardless of what's offered here. */
    options: string[];
}

export interface ServiceAction {
    /** Exact action name — must byte-match host-broker's allowlisted route,
     *  e.g. "nexus-cache-invalidate". */
    name: string;
    /** Button label, e.g. "Clear Nexus cache". */
    label: string;
    /** One-line explanation shown in the confirm step. */
    description: string;
    /** Parameters the user picks before confirming. Empty for a no-param action. */
    params: ServiceActionParam[];
}

export interface Service {
    /** Container name as it appears in `docker ps` (the key we match status on),
     *  or a synthetic identifier for non-container (host/timer) entries. */
    name: string;
    /** Human label if the container name isn't self-explanatory. */
    label?: string;
    /** container = long-running docker container (participates in up/down);
     *  timer   = periodic systemd timer/job (not expected to be "up");
     *  host    = host-level service, not a docker container. */
    kind: ServiceKind;
    /** Optional one-liner shown on hover. */
    note?: string;
    /** Actions available from this service's detail page (HOME-188). Absent
     *  or empty = metrics/info only, no actions offered — not every service
     *  needs one. Attached editorially (see SERVICE_ACTIONS below) rather than
     *  generated, since an action is structured data, not a `@overview` marker. */
    actions?: ServiceAction[];
}

export interface BoxMeta {
    id: 'luke' | 'leia' | 'cal';
    name: string;
    /** Short role tagline. */
    role: string;
    /** What this box is responsible for, in plain terms. */
    responsibilities: string[];
    /** The box's own docker-status endpoint (home LAN). */
    statusUrl: string;
    /** The box's own live volume-layout endpoint (home LAN, HOME-209). */
    volumesUrl: string;
    /** The box's own action-relay endpoint base — `POST {actionsUrl}/<name>`
     *  (HOME-188, relayed by the status container to host-broker). */
    actionsUrl: string;
    /** Runtime root filesystem is mounted read-only (writes only via an Ansible
     *  remount-rw window) — enforces IaC: no ad-hoc changes on the box. */
    roRoot: boolean;
}

export interface Box extends BoxMeta {
    services: Service[];
}

const statusUrl = (host: string) => `https://api.${host}.home.nakomis.com/docker/status`;
const volumesUrl = (host: string) => `https://api.${host}.home.nakomis.com/volumes`;
const actionsUrl = (host: string) => `https://api.${host}.home.nakomis.com/actions`;

// The conversation-memory ingest queue/DLQ-depth endpoint (HOME-198,
// served by embedding-consumer on Cal) used to be read directly by the
// browser from here (CONVMEM_STATUS_URL). As of HOME-243 that pull happens
// server-side in the portal API's GET /overview
// (infra/cdk/lambdas/portal-overview/src/handlers/overview.ts), which
// hardcodes the same URL — there is no code-level link between the two,
// same caveat as NEXUS_REPOS below.

// Editorial box-level metadata only — no `services` here. See file header.
const BOX_META: BoxMeta[] = [
    {
        id: 'luke',
        name: 'Luke',
        role: 'Application host',
        responsibilities: [
            'Taiga — self-hosted project management',
            'NAS shares (Samba)',
            'Volatile scratch filesystem',
            'Nightly Taiga backups to S3',
        ],
        statusUrl: statusUrl('luke'),
        volumesUrl: volumesUrl('luke'),
        actionsUrl: actionsUrl('luke'),
        roRoot: true,
    },
    {
        id: 'leia',
        name: 'Leia',
        role: 'Public gateway / NAS',
        responsibilities: [
            'nginx reverse proxy for *.home.nakomis.com (+ wildcard TLS)',
            'Time Machine / NAS (Samba)',
            'Nexus package registry (packages.home.nakomis.com)',
            'OctoPrint — 3D printer control',
            'Guacamole — remote desktop gateway',
            'TOTP (nakotp), OpenSearch, disk-health UI',
        ],
        statusUrl: statusUrl('leia'),
        volumesUrl: volumesUrl('leia'),
        actionsUrl: actionsUrl('leia'),
        roRoot: true,
    },
    {
        id: 'cal',
        name: 'Cal',
        role: 'Backoffice / embeddings',
        responsibilities: [
            'claude-chats embeddings (Ollama, mxbai-q8 — ~1.7s/embed)',
            'Status bridge — read-only docker-status via Leia mTLS gate',
            'Scrutiny disk-health collector → Leia hub',
            'conversation-memory pgvector Postgres (database of record, HOME-183)',
            'Async SQS embedding consumer (HOME-183)',
        ],
        statusUrl: statusUrl('cal'),
        volumesUrl: volumesUrl('cal'),
        actionsUrl: actionsUrl('cal'),
        roRoot: true,
    },
];

// The exact proxy-repo names `leia/ansible/playbook.yml`'s `nexus_repos`
// provisions — kept in lockstep with `host-broker`'s own
// `nexus_cache_invalidate.rs::ALLOWED_REPOS` by hand, same caveat as that
// constant: no code-level link between them, so a repo renamed there needs
// updating here (and vice versa).
const NEXUS_REPOS = [
    'docker-hub',
    'npm-proxy',
    'pypi-proxy',
    'maven-central',
    'gradle-plugins',
    'cargo-proxy',
    'ollama-raw',
    'apt-ubuntu-archive',
    'apt-ubuntu-security',
];

// Editorial per-service actions (HOME-188), keyed by box id → generated service
// name. An action is structured data (label, params) that can't be expressed as
// an `@overview` marker, so — unlike label/note/kind, which are generated — it's
// overlaid here onto the generated service of the matching name. A name that
// matches no generated service is silently ignored: this cannot add or rename a
// service, only annotate one that the generator already produced (see header).
const SERVICE_ACTIONS: Record<string, Record<string, ServiceAction[]>> = {
    leia: {
        nexus: [{
            name: 'nexus-cache-invalidate',
            label: 'Clear Nexus cache',
            description: 'Invalidate Nexus\'s negative/positive cache for one proxy repo '
                + '(host-broker HOME-213 action, relayed via this box\'s status container).',
            params: [{ name: 'repo', label: 'Repository', options: NEXUS_REPOS }],
        }],
    },
};

export const BOXES: Box[] = BOX_META.map(meta => {
    const generated = GENERATED_BOXES.find(g => g.id === meta.id);
    if (!generated) {
        throw new Error(`infra.ts: no generated services found for box '${meta.id}' — is infra.generated.ts stale? Run \`npm run generate:infra\`.`);
    }
    const boxActions = SERVICE_ACTIONS[meta.id] ?? {};
    const services = generated.services.map(svc =>
        boxActions[svc.name] ? { ...svc, actions: boxActions[svc.name] } : svc,
    );
    return { ...meta, services };
});
